import React from "react";
import { useState, useEffect, useMemo } from "react";
import { TAKEOFF_DB } from "../lib/takeoffDb.js";
import { drawingsApi } from "../api/drawings.js";
import { businessData } from "../api/businessData.js";
import { ApiError } from "../api/client.js";
import { PaywallScreen } from "../billing/PaywallScreen.jsx";
import { TakeoffView } from "./TakeoffView.jsx";
import { KainininkasBigView } from "./KainininkasBigView.jsx";
import { SamataView } from "./SamataView.jsx";
import { ProfilisView } from "./ProfilisView.jsx";
import { ProjectsSidebar } from "./ProjectsSidebar.jsx";

const DEFAULT_PROFILE = { type: 'individual', fullName:'', address:'', email:'', phone:'', iban:'', bankName:'', swift:'', companyName:'', companyCode:'', pvmCode:'', director:'', ivNr:'', personalCode:'', notes:'' };
const DEFAULT_PROJECTS = [{ id: 1, title: 'Mano projektas', takeoffTemplates: [], takeoffTemplateGroups: [], takeoffDrawings: [], takeoffMarkings: [], takeoffLayers: [], takeoffRooms: [], takeoffFloorZones: [], takeoffHeatSettings: {}, takeoffCalibrations: {} }];

export default function BusinessDashboard() {
          const [activeTab, setActiveTab] = useState('takeoff');
          const [dataLoaded, setDataLoaded] = useState(false);
          const [showPaywall, setShowPaywall] = useState(false);
          const [paywallMessage, setPaywallMessage] = useState(null);

          // Profilis — synced with the server (per-business), not localStorage
          const [profilis, setProfilis] = useState(DEFAULT_PROFILE);
          // Kainininkai (shared across app) — synced with the server
          const [kainininkai, setKainininkai] = useState([]);
          // Projects for Takeoff — synced with the server (drawing PDF bytes stay in browser IndexedDB)
          const [projects, setProjects] = useState(DEFAULT_PROJECTS);
          const [activeProjectId, setActiveProjectId] = useState(1);

          // Load all three slices once on mount. `dataLoaded` gates the save-effects
          // below so we never PUT the pre-fetch default/empty state back to the
          // server before the real data has arrived.
          useEffect(() => {
            let cancelled = false;
            (async () => {
              try {
                const [profile, catalogs, projectsFromServer] = await Promise.all([
                  businessData.getProfile(),
                  businessData.getCatalogs(),
                  businessData.getProjects(),
                ]);
                if (cancelled) return;
                setProfilis(profile || DEFAULT_PROFILE);
                setKainininkai(catalogs || []);
                setProjects(projectsFromServer && projectsFromServer.length > 0 ? projectsFromServer : DEFAULT_PROJECTS);
              } catch (e) {
                if (e instanceof ApiError && e.status === 402) {
                  setShowPaywall(true);
                  setPaywallMessage(e.body?.message || null);
                } else {
                  console.error('Failed to load business data', e);
                }
              } finally {
                if (!cancelled) setDataLoaded(true);
              }
            })();
            return () => { cancelled = true; };
          }, []);

          useEffect(() => {
            if (!dataLoaded) return;
            businessData.putProfile(profilis).catch(e => console.error('Failed to save profilis', e));
          }, [profilis, dataLoaded]);

          useEffect(() => {
            if (!dataLoaded) return;
            businessData.putCatalogs(kainininkai).catch(e => console.error('Failed to save kainininkai', e));
          }, [kainininkai, dataLoaded]);

          useEffect(() => {
            if (!dataLoaded) return;
            try {
              const stripped = projects.map(p => ({...p, takeoffDrawings: (p.takeoffDrawings||[]).map(({_pdfBuf,...r})=>r)}));
              businessData.putProjects(stripped).catch(e => console.error('Failed to save projects', e));
            } catch(e) {}
          }, [projects, dataLoaded]);

          // Brėžinių pasirinkimas — sinchronizuojamas tarp ProjectsSidebar ir TakeoffView
          const [sharedSelectedDrawingId, setSharedSelectedDrawingId] = useState(null);
          const takeoffFileInputRef = React.useRef(null);

          const activeProject = projects.find(p => p.id === activeProjectId) || projects[0];

          // Perjungus projektą — atstatyti pasirinktą brėžinį
          useEffect(() => { setSharedSelectedDrawingId(null); }, [activeProjectId]);

          function handleDeleteDrawing(drawingId) {
            const projectDrawings = activeProject?.takeoffDrawings || [];
            const drawing = projectDrawings.find(d => d.id === drawingId);
            TAKEOFF_DB.delete(drawingId).catch(()=>{});
            if (drawing?.serverId) {
              // Multi-page PDFs share one serverId across their page-drawings —
              // only delete server-side once no sibling page still references it.
              const stillReferenced = projectDrawings.some(d => d.id !== drawingId && d.serverId === drawing.serverId);
              if (!stillReferenced) {
                drawingsApi.remove(drawing.serverId).catch(e => console.warn('Failed to delete drawing from server', e));
              }
            }
            setProjects(prev => prev.map(p => p.id !== activeProjectId ? p : {
              ...p,
              takeoffDrawings: (p.takeoffDrawings||[]).filter(d => d.id !== drawingId),
              takeoffMarkings: (p.takeoffMarkings||[]).filter(m => m.drawingId !== drawingId),
            }));
            if (sharedSelectedDrawingId === drawingId) setSharedSelectedDrawingId(null);
          }
          // Suvestinė iš Takeoff — skaičiuojama iš aktyvaus projekto duomenų
          const lastTakeoffSummary = useMemo(() => {
            if (!activeProject) return [];
            const templates = activeProject.takeoffTemplates || [];
            const markings = activeProject.takeoffMarkings || [];
            const calibrations = activeProject.takeoffCalibrations || {};
            const floorZones = activeProject.takeoffFloorZones || [];
            const heatSettings = activeProject.takeoffHeatSettings || {};

            // Pagalbinė: ilgio skaičiavimas
            const lineLen = pts => { let l=0; for(let i=1;i<pts.length;i++){const dx=pts[i].x-pts[i-1].x,dy=pts[i].y-pts[i-1].y;l+=Math.sqrt(dx*dx+dy*dy);} return l; };

            const perTemplate = {};
            markings.forEach(m => {
              const tmpl = templates.find(t => t.id === m.templateId);
              if (!tmpl) return;
              // Kalibravimas šio brėžinio
              const cal = calibrations[m.drawingId];
              const scale = (cal && cal.pixelLength && cal.realLength) ? cal.realLength / cal.pixelLength : null;
              const isLinear = m.shape === 'line' || m.shape === 'polyline';
              const rawVal = isLinear ? lineLen(m.points || []) : 1;

              if (!perTemplate[tmpl.id]) perTemplate[tmpl.id] = { tmpl, positions: {}, count: 0 };
              perTemplate[tmpl.id].count++;

              (tmpl.positions || []).forEach(pos => {
                const key = pos.id || pos.itemId || pos.name;
                if (!perTemplate[tmpl.id].positions[key])
                  perTemplate[tmpl.id].positions[key] = { pos, total: 0 };
                let qty = 0;
                if (!isLinear || pos.unit === 'vnt.') {
                  qty = pos.qtyPerUnit * 1;
                } else {
                  const realLen = scale ? rawVal * scale : rawVal;
                  qty = realLen * pos.qtyPerUnit;
                }
                perTemplate[tmpl.id].positions[key].total += qty;
              });
            });

            // Grąžinti suplotintą sąrašą su visomis pozicijomis
            const result = [];
            Object.values(perTemplate).forEach(({ tmpl, positions }) => {
              Object.values(positions).forEach(({ pos, total }) => {
                result.push({
                  name: pos.name || tmpl.name,
                  unit: pos.unit || 'vnt.',
                  qty: Math.round(total * 1000) / 1000,
                  groupName: pos.groupName || tmpl.groupName || '',
                  itemId: pos.itemId || null,
                });
              });
            });
            return result;
          }, [activeProject]);

          const tabs = [
            { id: 'takeoff', label: '📏 Takeoff' },
            { id: 'kainininkas', label: '💰 Kainininkas' },
            { id: 'samata', label: '📊 Sąmata' },
            { id: 'profilis', label: '👤 Mano profilis' },
          ];

          // Lengvas "wave" paspaudimo efektas — be jokios bibliotekos
          function addRipple(e) {
            const button = e.currentTarget;
            const rect = button.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);
            const ripple = document.createElement('span');
            ripple.className = 'ripple';
            ripple.style.width = ripple.style.height = `${size}px`;
            ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
            ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
            button.appendChild(ripple);
            ripple.addEventListener('animationend', () => ripple.remove());
          }

          if (!dataLoaded) {
            return (
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',flexDirection:'column',gap:12,background:'#F5F6FA'}}>
                <div style={{width:40,height:40,border:'4px solid #e5e7eb',borderTopColor:'#735DFF',borderRadius:'50%',animation:'spin 0.8s linear infinite'}} />
                <div style={{fontSize:14,color:'#383853',fontWeight:600}}>Kraunami duomenys...</div>
              </div>
            );
          }

          if (showPaywall) {
            return <PaywallScreen message={paywallMessage} />;
          }

          return (
            <div style={{minHeight:'100vh',background:'#F5F6FA',padding:'10px'}}>
              {/* Navigacija */}
              <div className="nav-wrapper" style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginRight:8,flexShrink:0}}>
                  <div style={{width:28,height:28,borderRadius:8,background:'#735DFF',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0,boxShadow:'0 1px 2px rgba(0,0,0,0.15)'}}>📐</div>
                  <div style={{fontWeight:900,fontSize:15,color:'#735DFF',letterSpacing:1}}>SANNAMAI</div>
                </div>
                <span style={{fontSize:10,color:'#9ca3af',marginRight:12,flexShrink:0}}>v1.0</span>
                <div style={{display:'flex',gap:2,flexWrap:'wrap'}}>
                  {tabs.map(t => (
                    <button key={t.id} onClick={()=>setActiveTab(t.id)} onMouseDown={addRipple}
                      className={"btn-wave px-4 py-2 text-sm font-semibold transition-all rounded-lg "+(activeTab===t.id?'bg-ral3000 text-white':'bg-ral3000-subtle text-ral3000 hover:bg-ral3000-light')}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Turinys */}
              {activeTab === 'takeoff' && (
                <div style={{display:'flex',gap:0,alignItems:'flex-start'}}>
                  <ProjectsSidebar
                    projects={projects}
                    setProjects={setProjects}
                    activeProjectId={activeProjectId}
                    setActiveProjectId={setActiveProjectId}
                    selectedDrawingId={sharedSelectedDrawingId}
                    onSelectDrawing={id=>{setSharedSelectedDrawingId(id);}}
                    onDeleteDrawing={handleDeleteDrawing}
                    onUploadClick={()=>takeoffFileInputRef.current&&takeoffFileInputRef.current.click()}
                  />
                  {activeProject && (
                    <div style={{flex:1,minWidth:0}}>
                      <TakeoffView
                        activeProject={activeProject}
                        setProjects={setProjects}
                        projects={projects}
                        activeProjectId={activeProjectId}
                        ziniarastis={kainininkai}
                        setAtliktiDarbai={()=>{}}
                        atliktiDarbai={[]}
                        externalSelectedDrawingId={sharedSelectedDrawingId}
                        onSelectDrawing={id=>setSharedSelectedDrawingId(id)}
                        onDeleteDrawing={handleDeleteDrawing}
                        onUploadDrawing={drawings=>{
                          if(drawings&&drawings.length>0) setSharedSelectedDrawingId(drawings[0].id);
                        }}
                        externalFileInputRef={takeoffFileInputRef}
                      />
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'kainininkas' && (
                <KainininkasBigView kainininkai={kainininkai} setKainininkai={setKainininkai} />
              )}
              {activeTab === 'samata' && (
                <SamataView kainininkai={kainininkai} takeoffData={lastTakeoffSummary} />
              )}
              {activeTab === 'profilis' && (
                <ProfilisView profilis={profilis} setProfilis={setProfilis} />
              )}
            </div>
          );
        }
